import React, { useState } from "react";
import { Pencil, Check, X, MessageSquare, Send, Mail, Users } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "../../../utils/function";
import GlobalAdminStatCard from "../global-admin/stat-card";

// Hardcoded for now — no pricing endpoint exists yet. Once one does, this
// component's shape (DefaultRates + per-customer overrides) is what the
// backend response should mirror; only the two useState initializers below
// need to become a GET call, the rest of the component stays the same.
export interface DefaultRates {
  sms: number;
  whatsapp: number;
  email: number;
}

export interface CustomerPricingRow {
  id: string;
  name: string;
  sms: number;
  whatsapp: number;
  email: number;
}

const DEFAULT_RATES: DefaultRates = { sms: 4.5, whatsapp: 12, email: 1.2 };

const CUSTOMER_PRICING: CustomerPricingRow[] = [
  { id: "1", name: "Vouchlinks", sms: 4.5, whatsapp: 12, email: 1.2 },
  { id: "2", name: "Zenith Retail", sms: 5.0, whatsapp: 12, email: 1.2 },
  { id: "3", name: "Kudi Express", sms: 4.5, whatsapp: 11, email: 1.0 },
  { id: "4", name: "PayNest", sms: 4.8, whatsapp: 12.5, email: 1.2 },
  { id: "5", name: "Swift Logistics", sms: 4.5, whatsapp: 12, email: 1.5 },
];

const money = (n: number) => formatCurrency(n, "NGN", "symbol");

// A single editable rate field — used both for the three default-rate
// stat cards and (in a smaller inline form) for each per-customer row.
const RateField: React.FC<{
  value: number;
  editable: boolean;
  onSave: (next: number) => void;
}> = ({ value, editable, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        <span>{money(value)}</span>
        {editable && (
          <button
            type="button"
            onClick={() => {
              setDraft(String(value));
              setEditing(true);
            }}
            title="Edit rate"
            className="text-slate-300 hover:text-indigo-600 dark:text-gray-600 dark:hover:text-indigo-400"
          >
            <Pencil size={12} />
          </button>
        )}
      </div>
    );
  }

  const save = () => {
    const next = Number(draft);
    if (Number.isNaN(next) || next < 0) {
      toast.error("Enter a valid rate");
      return;
    }
    onSave(next);
    setEditing(false);
    toast.success("Pricing updated");
  };

  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        min={0}
        step="0.01"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
        className="w-20 px-2 py-1 text-sm border border-indigo-300 dark:border-indigo-500/50 bg-white dark:bg-gray-900 rounded-md outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/30"
      />
      <button onClick={save} title="Save" className="text-emerald-600 hover:text-emerald-700">
        <Check size={15} />
      </button>
      <button onClick={() => setEditing(false)} title="Cancel" className="text-slate-400 hover:text-slate-600">
        <X size={15} />
      </button>
    </div>
  );
};

// Shared by both the reseller's own dashboard (editable) and Global Admin's
// read-only Convo Marketing view (components/layouts/global-admin-sidebar +
// views/global-admin-pages/convo-marketing) — per the explicit requirement
// that global_admin only ever sees this module, and only as a viewer.
// Everything here is local React state seeded from the hardcoded constants
// above; there's no persistence yet, so a page refresh resets any edits.
const PricingDashboard: React.FC<{ editable: boolean }> = ({ editable }) => {
  const [defaults, setDefaults] = useState<DefaultRates>(DEFAULT_RATES);
  const [customers, setCustomers] = useState<CustomerPricingRow[]>(CUSTOMER_PRICING);

  const updateDefault = (channel: keyof DefaultRates, value: number) => {
    setDefaults((prev) => ({ ...prev, [channel]: value }));
  };

  const updateCustomer = (id: string, channel: keyof Omit<CustomerPricingRow, "id" | "name">, value: number) => {
    setCustomers((prev) => prev.map((c) => (c.id === id ? { ...c, [channel]: value } : c)));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-950 dark:via-gray-950 dark:to-gray-950 -m-4 p-4">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-2xl border border-slate-200 dark:border-gray-800 shadow-sm px-6 py-5 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shrink-0">
            <MessageSquare className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                Convo Marketing — Pricing
              </h1>
              {!editable && (
                <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/15 px-2 py-0.5 rounded-full">
                  Read-only
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 dark:text-gray-400">
              {editable
                ? "Default pricing per customer for SMS, WhatsApp and Email."
                : "View-only — pricing is managed by resellers, not editable here."}
            </p>
          </div>
        </div>
      </div>

      {/* Default rates */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          Default Rates (apply unless a customer has an override below)
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <GlobalAdminStatCard
            label="SMS"
            value={<RateField value={defaults.sms} editable={editable} onSave={(v) => updateDefault("sms", v)} />}
            icon={<Send className="w-6 h-6 text-indigo-600" />}
            iconClassName="bg-indigo-100 dark:bg-indigo-500/15"
          />
          <GlobalAdminStatCard
            label="WhatsApp"
            value={<RateField value={defaults.whatsapp} editable={editable} onSave={(v) => updateDefault("whatsapp", v)} />}
            icon={<MessageSquare className="w-6 h-6 text-emerald-600" />}
            iconClassName="bg-emerald-100 dark:bg-emerald-500/15"
          />
          <GlobalAdminStatCard
            label="Email"
            value={<RateField value={defaults.email} editable={editable} onSave={(v) => updateDefault("email", v)} />}
            icon={<Mail className="w-6 h-6 text-blue-600" />}
            iconClassName="bg-blue-100 dark:bg-blue-500/15"
          />
        </div>
      </section>

      {/* Per-customer overrides */}
      <section>
        <h2 className="text-sm font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
          <Users size={14} /> Per-Customer Pricing
        </h2>
        <div className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 dark:border-gray-800 text-left text-xs font-semibold text-slate-400 dark:text-gray-500 uppercase tracking-wide">
                <th className="px-6 py-3">Customer</th>
                <th className="px-6 py-3">SMS</th>
                <th className="px-6 py-3">WhatsApp</th>
                <th className="px-6 py-3">Email</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
              {customers.map((c) => (
                <tr key={c.id}>
                  <td className="px-6 py-3.5 font-medium text-slate-800 dark:text-gray-100">{c.name}</td>
                  <td className="px-6 py-3.5 text-slate-700 dark:text-gray-200">
                    <RateField value={c.sms} editable={editable} onSave={(v) => updateCustomer(c.id, "sms", v)} />
                  </td>
                  <td className="px-6 py-3.5 text-slate-700 dark:text-gray-200">
                    <RateField value={c.whatsapp} editable={editable} onSave={(v) => updateCustomer(c.id, "whatsapp", v)} />
                  </td>
                  <td className="px-6 py-3.5 text-slate-700 dark:text-gray-200">
                    <RateField value={c.email} editable={editable} onSave={(v) => updateCustomer(c.id, "email", v)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default PricingDashboard;
